import dayjs from 'dayjs';
import { DashboardState, FilterCondition, CommonRoutesType } from './types';

/** Tabular / filter column key; UI may use seconds or `01m:07s`, API expects seconds. */
export const AgentAgentInteractionDurationColumn = 'Agent_AgentInteractionDuration';

/** Dark shades for sentiment Sankey: green (positive), orange (neutral), red (negative) */
export const SENTIMENT_SANKEY_COLORS = [
  '#15803d', '#166534', '#16a34a', // dark greens - positive
  '#c2410c', '#ea580c', '#b45309', // dark oranges - neutral
  '#b91c1c', '#991b1b', '#dc2626', // dark reds - negative
];

export const ResolutionBarChartOptions = {
  chart: {
    type: 'bar',
  },
  title: {
    text: '',
  },
  credits: {
    enabled: false,
  },
  xAxis: {
    categories: ['Resolution'],
    labels: {
      style: {
        fontSize: '12px',
      },
    },
  },
  yAxis: {
    title: {
      text: 'Frequency',
    },
  },
  series: [],
};

export const PercentageAreaPrimaryTopicChartOptions: Highcharts.Options = {
  chart: {
    type: 'area',
  },
  title: {
    text: '',
  },
  credits: {
    enabled: false,
  },
  xAxis: {
    categories: [],
    gridLineWidth: 0,
  },
  yAxis: {
    title: {
      text: 'Percentage',
    },
    tickPositions: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
    gridLineWidth: 1,
  },
  plotOptions: {
    area: {
      stacking: 'percent',
      lineColor: 'none',
      lineWidth: 1,
      marker: {
        enabled: true,
      },
    },
  },
  legend: {
    layout: 'vertical',
    align: 'right',
    verticalAlign: 'top',
    borderWidth: 0,
    itemWidth: 100,
  },
  series: [],
};

export const PercentageAreaCustomerIntentChartOptions: Highcharts.Options = {
  chart: {
    type: 'area',
  },
  title: {
    text: '',
  },
  credits: {
    enabled: false,
  },
  xAxis: {
    categories: [],
    gridLineWidth: 0,
  },
  yAxis: {
    title: {
      text: 'Percentage',
    },
    tickPositions: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
    gridLineWidth: 1,
  },
  plotOptions: {
    area: {
      stacking: 'percent',
      lineColor: 'none',
      lineWidth: 1,
      marker: {
        enabled: true,
      },
    },
  },
  legend: {
    layout: 'vertical',
    align: 'right',
    verticalAlign: 'top',
    borderWidth: 0,
    itemWidth: 100,
  },
  series: [],
};

export const SankeyDiagram1Options = {
  chart: {
    type: 'sankey',
  },
  credits: {
    enabled: false,
  },
  title: {
    text: '',
  },
  series: [],
};

export const SankeyDiagram2Options: Highcharts.Options = {
  chart: {
    type: 'sankey',
  },
  credits: {
    enabled: false,
  },
  title: {
    text: '',
  },
  colors: SENTIMENT_SANKEY_COLORS,
  plotOptions: {
    sankey: {
      colors: SENTIMENT_SANKEY_COLORS,
    },
  },
  legend: {
    layout: 'vertical',
    align: 'left',
    verticalAlign: 'middle',
  },
  series: [],
};

export const PrimaryTopicBarChartOptions = {
  chart: {
    type: 'bar',
  },
  title: {
    text: '',
  },
  credits: {
    enabled: false,
  },
  xAxis: {
    categories: ['Primary Topic'],
    labels: {
      style: {
        fontSize: '12px',
      },
    },
  },
  yAxis: {
    title: {
      text: 'Frequency',
    },
  },
  series: [],
};

export const Nodes = [
  {
    id: 'Customer Sentiment At Start: Negative',
    name: ' Negative ',
    color: '#d4d4d4'
  },
  {
    id: 'Customer Sentiment At End: Negative',
    name: ' Negative ',
    color: '#d4d4d4'
  },
  {
    id: 'Customer Sentiment At Start: Positive',
    name: ' Positive ',
    color: '#d4d4d4'
  },
  {
    id: 'Customer Sentiment At End: Positive',
    name: ' Positive ',
    color: '#d4d4d4'
  },
  {
    id: 'Customer Sentiment At Start: Neutral',
    name: ' Neutral ',
    color: '#d4d4d4'
  },
  {
    id: 'Customer Sentiment At End: Neutral',
    name: ' Neutral ',
    color: '#d4d4d4'
  },
];

export const LocaleTime = {
  firstDay: 1,
  startDate: dayjs().startOf('day'),
  endDate: dayjs().endOf('day'),
  format: 'YYYY-MM-DD',
  separator: ' to ',
  daysOfWeek: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
  monthNames: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  applyLabel: 'Apply',
  cancelLabel: 'Cancel',
  fromLabel: 'From',
  toLabel: 'To',
};

export const Locale = {
  firstDay: 1,
  startDate: dayjs().startOf('day'),
  endDate: dayjs().endOf('day'),
  format: 'YYYY-MM-DD',
  separator: ' to ',
  daysOfWeek: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
  monthNames: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  applyLabel: 'Apply',
  cancelLabel: 'Cancel',
  fromLabel: 'From',
  toLabel: 'To',
};

export const DropdownSettings = {
  singleSelection: false,
  idField: 'filterId',
  textField: 'filterText',
  selectAllText: 'Select All',
  unSelectAllText: 'Unselect All',
  itemsShowLimit: 3,
  allowSearchFilter: true,
};

export const Durations = [
  { d_id: 4, name: 'Last 7 Days' },
  { d_id: 0, name: 'This Month' },
  { d_id: 1, name: 'Last Month' },
  { d_id: 2, name: 'Last Three Months' },
  { d_id: 3, name: 'Last Six Months' },
  { d_id: 5, name: 'Custom' },
];

export const DurationDropdownSettings = {
  singleSelection: true,
  idField: 'd_id',
  textField: 'name',
  itemsShowLimit: 1,
  allowSearchFilter: false,
  showSelectedItemsAtTop: false,
  defaultOpen: false,
  maxHeight: 130,
};

export const OperatorContent = [
  { label: 'And', value: 'And' },
  { label: 'Or', value: 'Or' },
];

export const FILTER_OPERATIONS = {
  string: [
    { label: 'contains', value: 'contains' },
    { label: 'does not contain', value: 'does_not_contain' },
    { label: 'contains either of', value: 'contains_either_of' },
    { label: 'is', value: 'is' },
    { label: 'is not', value: 'is_not' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' },
  ],
  date: [
    { label: 'on', value: 'on' },
    { label: 'before', value: 'before' },
    { label: 'after', value: 'after' },
    { label: 'on or before', value: 'on_or_before' },
    { label: 'on or after', value: 'on_or_after' },
    { label: 'date range', value: 'date_range' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' },
  ],
  integer: [
    { label: '=', value: '=' },
    { label: '≠', value: '!=' },
    { label: '>', value: '>' },
    { label: '<', value: '<' },
    { label: '>=', value: '>=' },
    { label: '<=', value: '<=' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' },
  ],
  cluster: [
    { label: 'contains', value: 'contains' },
    { label: 'contains either of', value: 'contains_either_of' },
    { label: 'is', value: 'is' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' },
  ], // Operations for cluster fields
  array: [
    { label: 'has any of', value: 'has_any_of' },
    { label: 'has all of', value: 'has_all_of' },
    { label: 'contains either of', value: 'contains_either_of' },
    { label: 'is exactly', value: 'is_exactly' },
    { label: 'has none of', value: 'has_none_of' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' },
  ],
  singleArray: [
    { label: 'is', value: 'is' },
    { label: 'is not', value: 'is_not' },
    { label: 'contains', value: 'contains' },
    { label: 'does not contain', value: 'does_not_contain' },
    { label: 'contains either of', value: 'contains_either_of' },
    { label: 'is empty', value: 'is_empty' },
    { label: 'is not empty', value: 'is_not_empty' },
  ],
};

export const Contains = 'contains';
export const DoesNotContain = 'does_not_contain';
export const ContainsEitherOf = 'contains_either_of';

export function isContainsEitherOfOperator(operation?: string | null): boolean {
  return operation === ContainsEitherOf;
}

/** Split/trim/dedupe values for contains_either_of. Existing single-value operators are not used here. */
export function normalizeContainsEitherOfValues(raw: unknown): string[] {
  if (raw == null || raw === '') return [];
  const parts = Array.isArray(raw) ? raw : String(raw).split(/[\n,]/);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const part of parts) {
    const value = String(part ?? '').trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(value);
  }
  return result;
}

export const DisableContent = {
  isEmpty: { label: 'is empty', value: 'is_empty' },
  isNotEmpty: { label: 'is not empty', value: 'is_not_empty' },
};

/** True when a filter operand has a usable value for its operator. */
export function hasFilterOperandValue(operation: string | undefined, raw: unknown): boolean {
  if (
    operation === DisableContent.isEmpty.value ||
    operation === DisableContent.isNotEmpty.value
  ) {
    return true;
  }
  if (isContainsEitherOfOperator(operation)) {
    return normalizeContainsEitherOfValues(raw).length > 0;
  }
  if (Array.isArray(raw)) {
    return raw.some((item) => String(item ?? '').trim().length > 0);
  }
  return raw != null && String(raw).trim().length > 0;
}

/** Normalize a cluster/text operand for the Insights/Nova request payload. */
export function serializeFilterOperandValue(
  operation: string | undefined,
  raw: unknown
): string | string[] {
  if (
    operation === DisableContent.isEmpty.value ||
    operation === DisableContent.isNotEmpty.value
  ) {
    return '';
  }
  if (isContainsEitherOfOperator(operation)) {
    return normalizeContainsEitherOfValues(raw);
  }
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item ?? '').trim()).filter(Boolean);
  }
  return raw == null ? '' : String(raw);
}

export const MonthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const InitialToggleValues = {
  date: false,
  filter: false,
  group: false,
  hideFields: false,
  moreOptions: false,
  sort: false,
  search: false,
  timeZone: false,
};

export const SaveFilterValues = [
  { label: 'option A', value: 'option A' },
  { label: 'option B', value: 'option B' },
];

export const InitialFilterData: FilterCondition = {
  index: 1,
  operator: null,
  selectedColumn: undefined,
  selectedOperation: undefined,
  value: '',
  isCluster: false,
  clusterData: [],
};

export const TruncateLimitCustomSelect = 20;

export const DateFormatColumns = [
  'ContactTimestamp',
  'Date',
  'Agent_ConnectedToAgentTimestamp',
  'Agent_AfterContactWorkEndTimestamp',
  'Agent_AfterContactWorkStartTimestamp',
  'Attributes_callbackDeQueuedTime',
  'Attributes_callbackEnQueuedTime',
  'ConnectedToSystemTimestamp',
  'DisconnectTimestamp',
  'InitiationTimestamp',
  'LastUpdateTimestamp',
  'Queue_DequeueTimestamp',
  'Queue_EnqueueTimestamp',
  'ScheduledTimestamp',
  'TransferCompletedTimestamp',
];

export const SingleArrayValues = [
  'CustomerSentimentAtStart',
  'CustomerSentimentAtEnd',
  'Intent',
  'PrimaryTopic',
  'CallerType',
  'CallResolution',
  'JourneyMoment',
  'Agent_Username',
  'Queue_Name',
];

/** Column condition values for Customer Sentiment At Start/End – predefined, not from backend */
export const CUSTOMER_SENTIMENT_PREDEFINED_VALUES = ['Positive', 'Neutral', 'Negative'] as const;

/** Column keys that use predefined sentiment values instead of API */
export const CUSTOMER_SENTIMENT_COLUMNS = ['CustomerSentimentAtStart', 'CustomerSentimentAtEnd'] as const;

export const FilterKeyMap: Record<string, string> = {
  CustomerSentimentAtStart: 'customersentimentatstart',
  CustomerSentimentAtEnd: 'customersentimentatend',
  Intent: 'intent',
  PrimaryTopic: 'primarytopic',
  CallerType: 'callertype',
  CallResolution: 'callresolution',
  JourneyMoment: 'journeymoment',
  TopicsClustered: 'TopicsClustered',
  Agent_Username: 'agent_username',
  Queue_Name: 'queue_name',
};

export const ReferenceDate = dayjs().subtract(1, 'day');
export const Ranges = {
  'Last 7 days': [ReferenceDate.subtract(6, 'days').startOf('day'), ReferenceDate.endOf('day')],
  'This month': [ReferenceDate.startOf('month'), ReferenceDate.endOf('month')],
  'Last 3 months': [
    ReferenceDate.subtract(3, 'months').startOf('month'),
    ReferenceDate.subtract(1, 'month').endOf('month'),
  ]
  // ,
  // 'Last 6 months': [
  //   ReferenceDate.subtract(6, 'months').startOf('month'),
  //   ReferenceDate.subtract(1, 'month').endOf('month'),
  // ],
} as any;

export const InitialDate = {
  startDate: Ranges['Last 7 days'][0],
  endDate: Ranges['Last 7 days'][1],
  label: 'Last 7 Days',
};

export const PopupConfirmationData = {
  deleteConfirmation: {
    title: 'Confirm Action',
    message: 'Are you sure you want to delete the filter?',
    iconUrl: 'assets/icons/warning_amber.svg',
    confirmBtnText: 'Delete',
  },
  deleteTaxonomyConfirmation: {
    title: 'Confirm Action',
    message: 'Are you sure you want to delete the item?',
    iconUrl: 'assets/icons/warning_amber.svg',
    confirmBtnText: 'Delete',
  },
  deleteTestConfirmation: {
    title: 'Confirm Deletion!',
    message: 'Are you sure you want to delete the test results?\n\nDeleted tests cannot be restored.',
    iconUrl: 'assets/icons/warning_amber.svg',
    confirmBtnText: 'Yes',
    cancelBtnText: 'Cancel',
  },
  cancelTaxonomyConfirmation: {
    title: 'Confirm Action',
    message: 'Are you sure you want to cancel changes?',
    iconUrl: 'assets/icons/warning_amber.svg',
    confirmBtnText: 'Yes',
    cancelBtnText: 'No',
  },
  deleteChartConfirmation: {
    title: 'Confirm Deletion',
    message: 'Are you sure you want to delete this chart? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
  },
};

export const ManageColumnCount = 20;

/** Default Insights Data View visible columns, in display order. */
export const DEFAULT_VISIBLE_COLUMN_ORDER = [
  'ContactTimestamp',
  'PrimaryTopic',
  'Intent',
  'Topics',
  'RootCause',
  'CustomerPainPoint',
  'Trigger',
  'Outcome',
  'Summary',
  'CallResolution',
  'CustomerSentimentAtStart',
  'CustomerSentimentAtEnd',
] as const;

export const DEFAULT_VISIBLE_COLUMN_LABELS: Record<string, string> = {
  ContactId: 'Contact ID',
  ContactTimestamp: 'Contact Timestamp',
  PrimaryTopic: 'Primary Topic',
  Intent: 'Intent',
  Topics: 'Topics',
  RootCause: 'Root Cause',
  CustomerPainPoint: 'Pain Point',
  Trigger: 'Trigger',
  Outcome: 'Outcome',
  Summary: 'Summary',
  CallResolution: 'Resolution',
  CustomerSentimentAtStart: 'Sentiment at Start',
  CustomerSentimentAtEnd: 'Sentiment at End',
};

export const MultiSelectorColumns = [
  'PrimaryTopic',
  'CustomerSentimentAtStart',
  'CustomerSentimentAtEnd',
  'Intent',
  'CallerType',
  'CallResolution',
  'JourneyMoment',
  'Agent_Username',
  'Queue_Name',
];

export enum ClusterColumns {
  RootCause = 'RootCause',
  CustomerPainPoint = 'CustomerPainPoint',
  Outcome = 'Outcome',
  Trigger = 'Trigger',
  /** Topics Cluster: same cluster filter UI as Root Cause; options from insights API TopicsClustered */
  TopicsClustered = 'TopicsClustered',
  Topics = 'Topics',
  RootCauseClustered = 'RootCauseClustered',
  CustomerPainPointClustered = 'CustomerPainPointClustered',
  OutcomeClustered = 'OutcomeClustered',
  TriggerClustered = 'TriggerClustered',
}

export enum ClusterNames {
  RootCause = 'RootCauseClustered',
  CustomerPainPoint = 'CustomerPainPointClustered',
  Outcome = 'OutcomeClustered',
  Trigger = 'TriggerClustered',
  TopicsClustered = 'TopicsClustered',
  Topics = 'TopicsClustered',
  RootCauseClustered = 'RootCauseClustered',
  CustomerPainPointClustered = 'CustomerPainPointClustered',
  OutcomeClustered = 'OutcomeClustered',
  TriggerClustered = 'TriggerClustered',
}

export interface ClusterFieldPair {
  rawKey: string;
  clusteredKey: string;
  rawLabel: string;
  clusteredLabel: string;
  clusteredType: 'string' | 'array';
}

/** Free-form vs cluster field pairs for Insights table + shared filter. */
export const CLUSTER_FIELD_PAIRS: ClusterFieldPair[] = [
  {
    rawKey: 'RootCause',
    clusteredKey: 'RootCauseClustered',
    rawLabel: 'Root Cause',
    clusteredLabel: 'Root Cause (Cluster)',
    clusteredType: 'string',
  },
  {
    rawKey: 'CustomerPainPoint',
    clusteredKey: 'CustomerPainPointClustered',
    rawLabel: 'Pain Point',
    clusteredLabel: 'Pain Point (Cluster)',
    clusteredType: 'string',
  },
  {
    rawKey: 'Trigger',
    clusteredKey: 'TriggerClustered',
    rawLabel: 'Trigger',
    clusteredLabel: 'Trigger (Cluster)',
    clusteredType: 'string',
  },
  {
    rawKey: 'Outcome',
    clusteredKey: 'OutcomeClustered',
    rawLabel: 'Outcome',
    clusteredLabel: 'Outcome (Cluster)',
    clusteredType: 'string',
  },
  {
    rawKey: 'Topics',
    clusteredKey: 'TopicsClustered',
    rawLabel: 'Topics',
    clusteredLabel: 'Topics (Cluster)',
    clusteredType: 'array',
  },
];

export const CLUSTER_FILTER_COLUMN_KEYS: string[] = CLUSTER_FIELD_PAIRS.map((pair) => pair.clusteredKey);

/** Cluster filter UI is opened from the clustered column, not the free-form column. */
export function isClusterFilterColumn(columnKey?: string | null): boolean {
  return !!columnKey && CLUSTER_FILTER_COLUMN_KEYS.includes(columnKey);
}

export function isClusteredInsightColumn(columnKey?: string | null): boolean {
  return isClusterFilterColumn(columnKey);
}

export function findClusterFieldPair(columnKey?: string | null): ClusterFieldPair | undefined {
  if (!columnKey) return undefined;
  return CLUSTER_FIELD_PAIRS.find((pair) => pair.rawKey === columnKey || pair.clusteredKey === columnKey);
}

export function getClusteredColumnKey(columnKey: string): string {
  return findClusterFieldPair(columnKey)?.clusteredKey ?? columnKey;
}

export function getRawColumnNameForClusterFilter(columnKey: string): string {
  return findClusterFieldPair(columnKey)?.rawKey ?? columnKey;
}

export function getClusterNameForColumn(columnKey: string): string | undefined {
  return findClusterFieldPair(columnKey)?.clusteredKey;
}

export function getInsightColumnDisplayName(columnKey: string, fallback?: string): string {
  if (columnKey === 'CustomerEndpoint_Address') return 'Customer Phone Number';
  if (columnKey === 'SystemEndpoint_Address') return 'Dialed Number';
  if (DEFAULT_VISIBLE_COLUMN_LABELS[columnKey]) return DEFAULT_VISIBLE_COLUMN_LABELS[columnKey];
  const pair = findClusterFieldPair(columnKey);
  if (pair?.rawKey === columnKey) return pair.rawLabel;
  if (pair?.clusteredKey === columnKey) return pair.clusteredLabel;
  return fallback || columnKey;
}

/** Nothing is excluded anymore — both free-form and cluster columns appear in the shared filter. */
export const COLUMNS_EXCLUDED_FROM_FILTER_DROPDOWN: string[] = [];

/** Clustered insight columns are shown in Data View; kept empty so existing callers stay valid. */
export const DATA_VIEW_HIDDEN_CLUSTER_COLUMN_KEYS: string[] = [];
export const EmptyDashboardState: DashboardState = {
  tableViewFlag: false,
  filters: [],
  dateRange: {
    ...InitialDate,
  },
  navBarData: {
    routeLink: 'conversation-insights',
    icon: 'assets/icons/bar_chart_icon.svg',
    label: 'Conversation Insights',
  },
};

export const CommonRoutes: CommonRoutesType[] = [
  { title: 'Insights Dashboard', routeLink: 'conversation-insights' },
  { title: 'Trends', routeLink: 'trends' },
  { title: 'User Dashboard', routeLink: 'user-dashboard' },
  { title: 'Create Dashboard', routeLink: 'create-dashboard', showBackButton: true },
  { title: 'Taxonomy Builder', routeLink: 'taxonomy-dashboard' },
  { title: 'All Taxonomies', routeLink: 'taxonomy-dashboard/all-taxonomies' },
  { title: 'Playground', routeLink: 'taxonomy-dashboard/playground' },
  { title: 'Freeform Collection', routeLink: 'freeform-collection' },
];

export const TIMEZONE_LIST = [
  { label: 'UTC', value: 'UTC' },
  { label: 'EST', value: 'EST' },
  { label: 'CST', value: 'CST' },
  { label: 'MST', value: 'MST' },
  { label: 'PST', value: 'PST' },
  { label: 'IST', value: 'IST' },
  { label: 'BST', value: 'BST' },
  { label: 'CET', value: 'CET' },
  { label: 'EET', value: 'EET' },
  { label: 'JST', value: 'JST' },
  { label: 'AEST', value: 'AEST' },
];

// #frontend timezone abbreviations to valid pytz timezones
const TIMEZONE_MAPPING = {
  UTC: 'UTC',
  EST: 'America/Toronto',
  CST: 'America/Chicago',
  MST: 'America/Denver',
  PST: 'America/Los_Angeles',
  IST: 'Asia/Kolkata',
  BST: 'Europe/London',
  CET: 'Europe/Paris',
  EET: 'Europe/Bucharest',
  JST: 'Asia/Tokyo',
  AEST: 'Australia/Sydney',
};
